<script setup lang="ts">
import { computed, h, ref, type VNode } from 'vue';
import { NTooltip, dataTableProps, type DataTableColumns } from 'naive-ui';
import GaiaPagination from '../composite/GaiaPagination.vue';
import { gaiaFontSize, gaiaNeutral, gaiaTracking } from '../themes/tokens';

const props = defineProps({
  ...dataTableProps,
  /**
   * Truncate a header that outgrows its column and put the full text in a
   * tooltip. On by default: a data table's column widths are driven by its
   * content, and a long header (an extraction prompt's name, a CM column) would
   * otherwise either wrap the header band to two lines or push the column wider
   * than its values need. Columns with a render-function title are left alone.
   */
  ellipsisHeader: {
    type: Boolean,
    default: true,
  },
  // The CM mocks put the outer border on the card (this wrapper's CSS), so
  // naive's own outer border stays off; row/header hairlines come from the
  // DataTable theme section.
  bordered: {
    type: Boolean,
    default: false,
  },
});

/**
 * The slice of a naive pagination config this wrapper actually reads. Naive's
 * PaginationProps carries much more; anything else a caller passes (prefix
 * renderers, slots) is superseded by GaiaPagination's own layout.
 */
interface PaginationConfigLike {
  page?: number;
  defaultPage?: number;
  pageSize?: number;
  defaultPageSize?: number;
  itemCount?: number;
  pageSizes?: unknown[];
  showSizePicker?: boolean;
  onUpdatePage?: (page: number) => void;
  'onUpdate:page'?: (page: number) => void;
  onUpdatePageSize?: (size: number) => void;
  'onUpdate:pageSize'?: (size: number) => void;
}

/**
 * Every Gaia table paginates through GaiaPagination — the footer strip from the
 * mocks (range left, Rows selector and 28px page squares right) — instead of
 * naive's own n-pagination, so the strip looks the same on every page. The
 * caller keeps passing the naive-style `pagination` config; this adapter reads
 * it, renders GaiaPagination in the card's footer, and (for non-remote tables,
 * where n-data-table used to do it) slices the rows itself.
 */
const paginationConfig = computed<PaginationConfigLike | null>(() =>
  props.pagination && typeof props.pagination === 'object'
    ? (props.pagination as PaginationConfigLike)
    : null
);

// Fallback state for uncontrolled configs (no `page`/`onUpdatePage`): naive
// used to own that state internally, so now the wrapper does.
const innerPage = ref(1);
const innerPageSize = ref(0);

const activePage = computed(() => paginationConfig.value?.page ?? innerPage.value);
const activePageSize = computed(() => {
  const config = paginationConfig.value;
  if (config?.pageSize !== undefined) return config.pageSize;
  if (innerPageSize.value > 0) return innerPageSize.value;
  return config?.defaultPageSize ?? 10;
});
const totalRows = computed(() => paginationConfig.value?.itemCount ?? props.data.length);
/** No sizes means no Rows selector — mirrors naive, where the picker is opt-in. */
const activePageSizes = computed<number[]>(() => {
  const config = paginationConfig.value;
  if (!config?.showSizePicker) return [];
  const sizes = (config.pageSizes ?? [5, 10, 25, 50]).filter(
    (size): size is number => typeof size === 'number'
  );
  return sizes;
});

function setPage(page: number): void {
  const config = paginationConfig.value;
  const handler = config?.onUpdatePage ?? config?.['onUpdate:page'];
  if (handler) handler(page);
  else innerPage.value = page;
}

function setPageSize(size: number): void {
  const config = paginationConfig.value;
  const handler = config?.onUpdatePageSize ?? config?.['onUpdate:pageSize'];
  if (handler) handler(size);
  else {
    innerPageSize.value = size;
    innerPage.value = 1;
  }
}

/** Non-remote tables relied on n-data-table to slice; now the wrapper does. */
const pagedData = computed(() => {
  if (!paginationConfig.value || props.remote) return props.data;
  const start = (activePage.value - 1) * activePageSize.value;
  return props.data.slice(start, start + activePageSize.value);
});

// `columns` is rewritten and `data`/`pagination` are adapted above, so none of
// the three is spread onto n-data-table verbatim; `ellipsisHeader` is ours.
const passThrough = computed(() => {
  const { ellipsisHeader, columns, data, pagination, ...rest } = props;
  return rest;
});

function ellipsisTitle(titleText: string): () => VNode {
  return (): VNode =>
    h(
      NTooltip,
      { trigger: 'hover', placement: 'top', delay: 300 },
      {
        trigger: (): VNode =>
          h(
            'span',
            {
              style:
                'display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:100%',
            },
            titleText
          ),
        default: (): string => titleText,
      }
    );
}

/**
 * Reaches into column groups, because in a grouped table the real headers are
 * the leaves: a playbook run's evaluation columns sit under one group per type,
 * and left unclipped their names wrap to three lines each — turning the header
 * band into ~180px of chrome that eats the rows below it. One level deep is all
 * naive's column groups nest.
 */
function withEllipsisHeaders(columns: DataTableColumns): DataTableColumns {
  return columns.map(col => {
    if ('children' in col) {
      return {
        ...col,
        ...(typeof col.title === 'string' ? { title: ellipsisTitle(col.title) } : {}),
        children: col.children.map(child =>
          typeof child.title === 'string' ? { ...child, title: ellipsisTitle(child.title) } : child
        ),
      };
    }
    if (col.type === 'selection' || col.type === 'expand') return col;
    return typeof col.title === 'string' ? { ...col, title: ellipsisTitle(col.title) } : col;
  });
}

const headerColumns = computed(() =>
  props.ellipsisHeader ? withEllipsisHeaders(props.columns) : props.columns
);

const cardBorder = gaiaNeutral.borderCard;
const cardBg = gaiaNeutral.surface;
const footerBg = gaiaNeutral.surfaceSoft;
const footerBorder = gaiaNeutral.borderDivider;
// The header casing can't be a GaiaText prop — it applies to naive's own th
// title element — so the tokens reach it through v-bind instead.
const headerSize = gaiaFontSize.micro;
const headerTracking = gaiaTracking.label;
</script>

<template>
  <!-- The CM table card from the CM list mock: a themed n-data-table
       (DataTable section in gaia-light.json) inside a bordered radius-10 card,
       with GaiaPagination as its gray footer strip. -->
  <div class="gaia-table">
    <n-data-table
      v-bind="passThrough"
      :columns="headerColumns"
      :data="pagedData"
      :pagination="false"
      class="gaia-table__table"
    >
      <template v-if="$slots.empty" #empty>
        <slot name="empty" />
      </template>
      <template v-if="$slots.loading" #loading>
        <slot name="loading" />
      </template>
    </n-data-table>

    <div v-if="paginationConfig && totalRows > 0" class="gaia-table__footer">
      <gaia-pagination
        :page="activePage"
        :page-size="activePageSize"
        :total="totalRows"
        :page-sizes="activePageSizes"
        @update:page="setPage"
        @update:page-size="setPageSize"
      />
    </div>
  </div>
</template>

<style scoped>
/* Card chrome + header casing + footer strip. None of these are DataTable
   theme vars (no text-transform/letter-spacing vars; the pagination strip is
   layout, not theme), so they live here as the sanctioned CSS exception. */
/* A flex column so a caller-set height (flex-height tables) reaches the
   n-data-table: the table takes the free space, the footer keeps its strip. */
.gaia-table {
  display: flex;
  flex-direction: column;
  border: 1px solid v-bind(cardBorder);
  border-radius: 10px;
  overflow: hidden;
  background: v-bind(cardBg);
}

.gaia-table__table {
  flex: 1 1 auto;
  min-height: 0;
}

.gaia-table__footer {
  flex: none;
}

.gaia-table :deep(.n-data-table-th .n-data-table-th__title) {
  text-transform: uppercase;
  letter-spacing: v-bind(headerTracking);
  font-size: v-bind(headerSize);
}

/* A header that is both sortable and filterable: naive keeps the sort arrow
   clear of its absolutely positioned 36px filter funnel with
   `padding-right: calc(var(--n-th-padding) + 36px)`. Our DataTable theme sets
   `thPaddingMedium` to a two-value shorthand ("12px 18px"), so that calc is
   invalid at computed-value time and padding-right falls back to 0 — the sort
   arrow lands underneath the funnel, where it can't be clicked. Restate the
   theme's padding (a plain var, no calc, so any shorthand survives) and reserve
   the funnel's band as a margin instead. */
.gaia-table :deep(.n-data-table-th--filterable.n-data-table-th--sortable) {
  padding: var(--n-th-padding);
}

.gaia-table
  :deep(.n-data-table-th--filterable.n-data-table-th--sortable .n-data-table-th__title-wrapper) {
  margin-right: 36px;
}

.gaia-table__footer {
  background: v-bind(footerBg);
  border-top: 1px solid v-bind(footerBorder);
}

/* pagination:false used to collapse the strip; keep the old rule harmless for
   any straggler that re-enables naive's own pagination via theme overrides. */
.gaia-table :deep(.n-data-table__pagination) {
  margin: 0;
  padding: 10px 18px;
}
</style>
